import {ArrowBack} from '@mui/icons-material';
import {Box, Chip, Container, IconButton, Paper, Typography, useTheme} from '@mui/material';
import React, {useContext, useEffect} from 'react';
import ReactMarkdown from 'react-markdown';
import {Link} from 'react-router-dom';
import ResponseGraphs from 'src/components/ResponseGraphs';
import {CoachContent, GrowthReportJsonBody} from '../object-actions/types/types';
import {ThemeContext} from '../theme/ThemeContext';
import {markAthleteReceived, markParentReceived} from '../utils/contentHelpers';
import {useRoleLabels} from "../hooks/useRoleLabels";

function formatSignedNumber(n: number | null | undefined): string {
    if (n == null || Number.isNaN(n)) return '—';
    const sign = n > 0 ? '+' : '';
    return `${sign}${n.toFixed(2)}`;
}

function GrowthReportStructured({json_body}: {json_body: GrowthReportJsonBody}) {
    const alignment = json_body.alignment;
    const overall = json_body.facts?.overall;
    const topDomains = json_body.facts?.top_domains ?? [];
    const lowestImprovedMost = json_body.facts?.lowest_baseline_improved_most;

    return (
        <Box data-testid="growth-report--summary" sx={{mb: 4}}>
            {alignment && (
                <Box
                    data-testid="growth-report--alignment"
                    data-attr={alignment.level ?? 'unknown'}
                    sx={{mb: 3}}
                >
                    <Typography variant="overline" display="block">Alignment Level</Typography>
                    <Chip
                        data-testid="growth-report--alignment-level"
                        data-attr={alignment.level ?? 'unknown'}
                        label={alignment.level ?? 'Unavailable'}
                        color={
                            alignment.level === 'High'
                                ? 'success'
                                : alignment.level === 'Moderate'
                                    ? 'primary'
                                    : 'default'
                        }
                    />
                    {alignment.reason && (
                        <Typography
                            data-testid="growth-report--alignment-reason"
                            variant="body2"
                            sx={{mt: 1}}
                        >
                            {alignment.reason}
                        </Typography>
                    )}
                </Box>
            )}

            {overall && overall.delta != null && (
                <Box
                    data-testid="growth-report--overall"
                    data-attr={overall.benchmark_status ?? 'pending'}
                    sx={{mb: 3}}
                >
                    <Typography variant="overline" display="block">
                        Overall Growth (Pre {'->'} Post)
                    </Typography>
                    <Typography
                        data-testid="growth-report--overall-delta"
                        data-custom-id={String(overall.delta)}
                        variant="h4"
                        sx={{fontWeight: 'bold'}}
                    >
                        {formatSignedNumber(overall.delta)}
                    </Typography>
                    {overall.benchmark_status && (
                        <Chip
                            data-testid="growth-report--overall-status"
                            data-attr={overall.benchmark_status}
                            label={overall.benchmark_status}
                            size="small"
                            color={overall.benchmark_status === 'Met' ? 'success' : 'default'}
                        />
                    )}
                </Box>
            )}

            {topDomains.length > 0 && (
                <Box data-testid="growth-report--top-domains" sx={{mb: 3}}>
                    <Typography variant="overline" display="block">
                        Top Domain Improvements
                    </Typography>
                    {topDomains.map((d) => (
                        <Box
                            key={d.key}
                            data-testid="growth-report--top-domain-item"
                            data-attr={d.key}
                            data-custom-id={String(d.delta)}
                            sx={{mb: 0.5}}
                        >
                            <Typography variant="body1">
                                <strong>{d.label}:</strong> {formatSignedNumber(d.delta)}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}

            {lowestImprovedMost && (
                <Chip
                    data-testid="growth-report--lowest-baseline-improved-most"
                    data-attr="true"
                    label="Greatest gains in lowest-baseline domains"
                    color="success"
                    size="small"
                    sx={{mb: 2}}
                />
            )}
        </Box>
    );
}

interface CoachContentReadOnlyViewProps {
    entity: CoachContent;
    layout?: 'full' | 'compact';
}

interface DateViewCalendarProps {
    entity: CoachContent;
}

export function DateViewCalendar({entity}: DateViewCalendarProps) {
    const {applyRoleLabels} = useRoleLabels();

    function buildLabel(time: string | null) {
        return time ? new Date(time).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        }) : 'No';
    }

    return (
        <Box sx={{display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap'}} data-testid="coach-delivery-status"
             data-attr={entity.purpose}>
            <Chip
                label={`Created: ` + new Date(entity.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                })}
                color={'default'}
                size="small"
            />
            <Chip
                data-testid="coach"
                label={applyRoleLabels(`Coach Delivered: `) + buildLabel(entity.coach_delivered)}
                color={entity.coach_delivered ? 'success' : 'default'}
                size="small"
            />
            <Chip
                data-testid="athlete"
                label={applyRoleLabels(`Athlete Received: `) + buildLabel(entity.athlete_received)}
                color={entity.athlete_received ? 'success' : 'default'}
                size="small"
            />
            <Chip
                data-testid="parent"
                label={applyRoleLabels(`Guardian Received: `) + buildLabel(entity.parent_received)}
                color={entity.parent_received ? 'success' : 'default'}
                size="small"
            />
        </Box>
    );
}

const CoachContentReadOnlyView: React.FC<CoachContentReadOnlyViewProps> = ({
                                                                               entity,
                                                                               layout = 'full'
                                                                           }) => {
    const theme = useTheme();
    const {brandingSettings} = useContext(ThemeContext);


    // Mark as received when athlete or parent accesses the page (only if not already marked)
    useEffect(() => {
        const markAsReceived = async () => {
            try {
                if (entity.my_roles?.includes('athlete') && !entity.athlete_received) {
                    await markAthleteReceived(Number(entity.id));
                } else if (entity.my_roles?.includes('parent') && !entity.parent_received) {
                    await markParentReceived(Number(entity.id));
                }
            } catch (error) {
                console.error('Error marking content as received:', error);
                // Don't show error to user as this is a background operation
            }
        };

        markAsReceived();
    }, [entity.id, entity.athlete_received, entity.parent_received]);

    if (!entity || entity._type !== 'CoachContent') return null;

    const showHeader = layout === 'full';
    const showResponseGraphs = layout === 'full';
    const isCompact = layout === 'compact';

    const chartSubmissions = entity.chart_submissions?.length
        ? entity.chart_submissions
        : undefined;

    const content = (
        <Box data-testid={`coach-content-readonly--${entity.purpose}`}>
            {/* Back to progress report (when athlete context exists) */}
            {showHeader && entity.assignment?.id && (
                <Box sx={{mb: 2, display: 'flex', alignItems: 'center', gap: 1}}>
                    <IconButton
                        component={Link}
                        to={`/program-progress/${entity.assignment.id}`}
                        color="secondary"
                        aria-label="Back to progress report"
                    >
                        <ArrowBack/>
                    </IconButton>
                    <Typography component={Link} to={`/program-progress/${entity.assignment.id}`} variant="body2"
                                color="text.secondary"
                                sx={{textDecoration: 'none', '&:hover': {textDecoration: 'underline'}}}>
                        Back to progress report
                    </Typography>
                </Box>
            )}
            {/* Header */}
            {showHeader && (
                <Box sx={{mb: 4, display: 'flex', alignItems: 'flex-start', gap: 3}}>
                    <Box sx={{flex: 1, minWidth: 0}}>
                        <Typography
                            variant="h3"
                            component="h1"
                            sx={{
                                fontWeight: 'bold',
                                color: 'secondary.main',
                                mb: 1
                            }}
                        >
                            {entity.title}
                        </Typography>
                        <DateViewCalendar entity={entity} />
                    </Box>

                </Box>
            )}

            {/* ResponseGraphs Section */}
            {showResponseGraphs && (
                <ResponseGraphs
                    submissions={chartSubmissions}
                    entity={chartSubmissions ? undefined : entity.athlete}
                    title="Athlete Performance Overview"
                    height={250}
                />
            )}

            {['growth_report_week_12', 'growth_report_week_4', 'growth_report_week_8'].includes(
                String(entity.purpose)
            ) && entity.json_body && (
                <GrowthReportStructured json_body={entity.json_body as GrowthReportJsonBody} />
            )}

            {/* Content Body */}
            <Box sx={{mb: 4}} data-testid="coach-content-readonly-body">
                {
                    <Box
                        sx={{
                            '& p': {margin: 0},
                            '& p + p': {marginTop: 2},
                            '& h1, & h2, & h3, & h4, & h5, & h6': {
                                marginTop: 3,
                                marginBottom: 2,
                                fontWeight: 'bold',
                                color: 'text.primary',
                                fontFamily: brandingSettings?.typography?.fontFamily || theme.typography.fontFamily
                            },
                            '& ul, & ol': {
                                marginTop: 2,
                                marginBottom: 2,
                                paddingLeft: 3
                            },
                            '& li': {
                                marginBottom: 1
                            },
                            '& a': {
                                color: theme.palette.primary.main,
                                textDecoration: 'none',
                                '&:hover': {
                                    textDecoration: 'underline'
                                }
                            },
                            '& blockquote': {
                                borderLeft: `4px solid ${theme.palette.primary.main}`,
                                paddingLeft: 2,
                                marginLeft: 0,
                                fontStyle: 'italic',
                                color: 'text.secondary',
                                backgroundColor: brandingSettings?.palette?.[theme.palette.mode]?.paper?.main || (theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50'),
                                padding: 2,
                                borderRadius: 1
                            },
                            '& code': {
                                backgroundColor: brandingSettings?.palette?.[theme.palette.mode]?.paper?.main || (theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100'),
                                padding: '2px 4px',
                                borderRadius: 1,
                                fontFamily: 'monospace'
                            },
                            '& pre': {
                                backgroundColor: brandingSettings?.palette?.[theme.palette.mode]?.paper?.main || (theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100'),
                                padding: 2,
                                borderRadius: 1,
                                overflow: 'auto'
                            }
                        }}
                    >
                        <ReactMarkdown>{entity.body}</ReactMarkdown>
                    </Box>}
            </Box>
        </Box>
    );

    if (isCompact) {
        return content;
    }

    return (
        <Container maxWidth="lg" sx={{py: 4}}>
            <Paper elevation={2} sx={{p: 4, borderRadius: 2}}>
                {content}
            </Paper>
        </Container>
    );
};

export default CoachContentReadOnlyView;
